'use client';
import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { recordCheckIn } from '@/lib/userLearning';
import { saveCheckIn } from '@/lib/services/betprotectService';
import { useLanguage } from '@/contexts/LanguageContext';

const TRIGGERS = [
  { key: 'trigger-boredom', label: 'Boredom' },
  { key: 'trigger-stress', label: 'Work stress' },
  { key: 'trigger-financial', label: 'Financial worry' },
  { key: 'trigger-loneliness', label: 'Loneliness' },
  { key: 'trigger-social', label: 'Social pressure' },
  { key: 'trigger-celebration', label: 'Celebrating something' },
  { key: 'trigger-anxiety', label: 'Anxiety' },
  { key: 'trigger-boredom-eve', label: 'Evening restlessness' },
  { key: 'trigger-sports', label: 'Watching sport' },
  { key: 'trigger-ads', label: 'Gambling ads' },
];

const MOOD_LABELS: Record<number, string> = {
  1: 'Very low', 2: 'Low', 3: 'Okay', 4: 'Good', 5: 'Great',
};

const URGE_LABELS: Record<number, { label: string; color: string }> = {
  1: { label: 'No urge', color: 'text-green-600' },
  2: { label: 'Barely noticeable', color: 'text-green-600' },
  3: { label: 'Mild', color: 'text-green-500' },
  4: { label: 'Mild-moderate', color: 'text-amber-500' },
  5: { label: 'Moderate', color: 'text-amber-500' },
  6: { label: 'Noticeable', color: 'text-amber-600' },
  7: { label: 'Strong', color: 'text-orange-500' },
  8: { label: 'Very strong', color: 'text-red-500' },
  9: { label: 'Intense', color: 'text-red-600' },
  10: { label: 'Overwhelming', color: 'text-red-700' },
};

interface FormData {
  diaryText: string;
}

export default function CheckInForm() {
  const { t } = useLanguage();
  const [mood, setMood] = useState(3);
  const [urge, setUrge] = useState(2);
  const [selectedTriggers, setSelectedTriggers] = useState<string[]>([]);
  const [showAllTriggers, setShowAllTriggers] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>();

  const toggleTrigger = useCallback((key: string) => {
    setSelectedTriggers((prev) =>
      prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]
    );
  }, []);

  const visibleTriggers = showAllTriggers ? TRIGGERS : TRIGGERS.slice(0, 6);

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    // Feed into the iterative learning engine (localStorage)
    recordCheckIn({ mood, urge, triggers: selectedTriggers, diaryText: data.diaryText });

    // Save to Supabase
    try {
      await saveCheckIn({ mood, urge, triggers: selectedTriggers, diaryText: data.diaryText });
    } catch (err) {
      console.log('Supabase save failed, data saved locally:', err);
    }

    setIsSubmitting(false);
    setSubmitted(true);
    toast.success(t('checkin.success'));
    reset();
  };

  if (submitted) {
    return (
      <div className="bg-card rounded-xl border border-border p-8 text-center space-y-4 fade-in">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
          <CheckCircle2 size={32} className="text-green-600" />
        </div>
        <div>
          <h3 className="text-lg font-700 text-foreground">Check-in complete</h3>
          <p className="text-sm text-muted-foreground mt-1">
            You&apos;ve logged how you&apos;re feeling today. That takes courage — keep going.
          </p>
        </div>
        <div className="flex items-center justify-center gap-6 py-2">
          <div className="text-center">
            <p className="text-2xl font-800 text-foreground font-tabular">{mood}/5</p>
            <p className="text-xs text-muted-foreground mt-0.5">Mood</p>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="text-center">
            <p className={`text-2xl font-800 font-tabular ${URGE_LABELS[urge]?.color}`}>{urge}/10</p>
            <p className="text-xs text-muted-foreground mt-0.5">Urge intensity</p>
          </div>
          {selectedTriggers.length > 0 && (
            <>
              <div className="w-px h-8 bg-border" />
              <div className="text-center">
                <p className="text-2xl font-800 text-foreground font-tabular">{selectedTriggers.length}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Trigger{selectedTriggers.length !== 1 ? 's' : ''} noted</p>
              </div>
            </>
          )}
        </div>
        <div className="rounded-xl px-4 py-3 text-sm text-left" style={{ background: 'rgba(0,51,153,0.06)', border: '1px solid rgba(0,51,153,0.15)' }}>
          <p className="font-600 text-foreground text-xs mb-1">🧠 Your suggestions have been updated</p>
          <p className="text-xs text-muted-foreground">
            The algorithm has learned from today&apos;s check-in. Visit{' '}
            <a href="/exercises" className="text-primary font-600 underline">Exercises</a>{' '}
            to see personalised recommendations based on your mood and triggers.
          </p>
        </div>
        <button onClick={() => setSubmitted(false)} className="text-sm text-primary font-600 hover:underline">
          {t('checkin.edit')}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="bg-card rounded-xl border border-border divide-y divide-border">
      {/* Mood section */}
      <div className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-600 text-foreground mb-1">{t('checkin.mood.label')}</label>
          <p className="text-xs text-muted-foreground mb-4">{t('checkin.mood.hint')}</p>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground w-14">Very low</span>
            <input
              type="range" min={1} max={5} step={1} value={mood}
              onChange={(e) => setMood(Number(e.target.value))}
              className="mood-track flex-1" aria-label="Mood level"
            />
            <span className="text-xs text-muted-foreground w-10 text-right">Great</span>
          </div>
          <div className="flex justify-between mt-2 px-14">
            {[1, 2, 3, 4, 5].map((v) => (
              <button
                key={`mood-pip-${v}`} type="button" onClick={() => setMood(v)}
                className={`text-xs font-500 transition-all duration-150 ${mood === v ? 'text-primary font-700 scale-110' : 'text-muted-foreground'}`}
              >{v}</button>
            ))}
          </div>
          <p className="text-center text-sm font-600 text-foreground mt-2">{MOOD_LABELS[mood]}</p>
        </div>
      </div>

      {/* Urge section */}
      <div className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-600 text-foreground mb-1">{t('checkin.urge.label')}</label>
          <p className="text-xs text-muted-foreground mb-4">{t('checkin.urge.hint')}</p>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground w-14">No urge</span>
            <input
              type="range" min={1} max={10} step={1} value={urge}
              onChange={(e) => setUrge(Number(e.target.value))}
              className="urge-track flex-1" aria-label="Urge intensity"
            />
            <span className="text-xs text-muted-foreground w-10 text-right">High</span>
          </div>
          <div className="mt-3 text-center">
            <span className={`text-3xl font-800 font-tabular ${URGE_LABELS[urge]?.color}`}>{urge}</span>
            <span className={`text-sm font-500 ml-2 ${URGE_LABELS[urge]?.color}`}>— {URGE_LABELS[urge]?.label}</span>
          </div>
          {urge >= 7 && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 font-500">
              That sounds tough. Would you like to try a quick breathing exercise?{' '}
              <a href="/exercises" className="underline font-700 hover:no-underline">Open exercises</a>
            </div>
          )}
        </div>
      </div>

      {/* Triggers */}
      <div className="p-6 space-y-3">
        <label className="block text-sm font-600 text-foreground">{t('checkin.triggers.label')}</label>
        <p className="text-xs text-muted-foreground">{t('checkin.triggers.hint')}</p>
        <div className="flex flex-wrap gap-2">
          {visibleTriggers.map((trigger) => (
            <button
              key={trigger.key} type="button" onClick={() => toggleTrigger(trigger.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-600 border transition-all duration-150 active:scale-95 ${
                selectedTriggers.includes(trigger.key)
                  ? 'border-primary text-primary bg-primary/10' :'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
              }`}
            >{trigger.label}</button>
          ))}
        </div>
        {TRIGGERS.length > 6 && (
          <button
            type="button" onClick={() => setShowAllTriggers((p) => !p)}
            className="flex items-center gap-1 text-xs text-primary font-600 hover:underline mt-1"
          >
            {showAllTriggers ? <><ChevronUp size={13} /> Show fewer</> : <><ChevronDown size={13} /> Show all {TRIGGERS.length} triggers</>}
          </button>
        )}
      </div>

      {/* Diary */}
      <div className="p-6 space-y-3">
        <label htmlFor="diary-text" className="block text-sm font-600 text-foreground">{t('checkin.diary.label')}</label>
        <textarea
          id="diary-text"
          rows={3}
          placeholder="Write freely — this is just for you."
          className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow resize-none"
          {...register('diaryText')}
        />
      </div>

      {/* Submit */}
      <div className="p-6">
        <button
          type="submit" disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-600 hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              {t('checkin.submitting')}
            </>
          ) : t('checkin.submit')}
        </button>
      </div>
    </form>
  );
}